/**
 * Generic Chinese City Landing Page
 * Handles all /zh/:citySlug routes
 */
import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { ChineseHero } from "@/components/ChineseHero";
import { ChineseWhyUs } from "@/components/ChineseWhyUs";
import { ChineseTestimonials } from "@/components/ChineseTestimonials";
import { ChineseFAQ } from "@/components/ChineseFAQ";
import { ChineseFinalCTA } from "@/components/ChineseFinalCTA";
import { LocationContext, REGIONS } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { ChineseServices } from "@/components/ChineseServices";
import { ChineseReferFriend } from "@/components/ChineseReferFriend";

const CITY_DATA: Record<string, { name: string; zhName: string; regionSlug: keyof typeof REGIONS; testimonialRegion: "sacramento" | "la" | "san-francisco" | "san-jose" | "san-diego" | "bay-area" }> = {
  // Region hubs
  "contra-costa":      { name: "Contra Costa County",  zhName: "康特拉科斯塔县",  regionSlug: "contra-costa",    testimonialRegion: "bay-area" },
  "solano":            { name: "Solano County",         zhName: "索拉诺县",        regionSlug: "solano",          testimonialRegion: "bay-area" },
  "alameda":           { name: "Alameda County",        zhName: "阿拉米达县",      regionSlug: "alameda",         testimonialRegion: "bay-area" },
  "san-francisco":     { name: "San Francisco",         zhName: "旧金山",          regionSlug: "san-francisco",   testimonialRegion: "san-francisco" },
  "santa-clara":       { name: "Santa Clara County",    zhName: "圣克拉拉县",      regionSlug: "santa-clara",     testimonialRegion: "san-jose" },
  "marin":             { name: "Marin County",          zhName: "马林县",          regionSlug: "marin",           testimonialRegion: "san-francisco" },
  "orange-county":     { name: "Orange County",         zhName: "橙县",            regionSlug: "orange-county",   testimonialRegion: "san-diego" },
  "los-angeles":       { name: "Los Angeles County",    zhName: "洛杉矶县",        regionSlug: "los-angeles",     testimonialRegion: "la" },
  "san-diego":         { name: "San Diego",             zhName: "圣地亚哥",        regionSlug: "san-diego",       testimonialRegion: "san-diego" },
  // Sacramento Area
  "sacramento":        { name: "Sacramento",        zhName: "沙加缅度",     regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "roseville":         { name: "Roseville",         zhName: "罗斯维尔",     regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "folsom":            { name: "Folsom",            zhName: "福尔瑟姆",     regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "elk-grove":         { name: "Elk Grove",         zhName: "艾尔克格罗夫", regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "davis":             { name: "Davis",             zhName: "戴维斯",       regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "rocklin":           { name: "Rocklin",           zhName: "罗克林",       regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "granite-bay":       { name: "Granite Bay",       zhName: "花岗岩湾",     regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "el-dorado-hills":   { name: "El Dorado Hills",   zhName: "埃尔多拉多山", regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "west-sacramento":   { name: "West Sacramento",   zhName: "西萨克拉门托", regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "rancho-cordova":    { name: "Rancho Cordova",    zhName: "兰乔科多瓦",   regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "carmichael":        { name: "Carmichael",        zhName: "卡迈克尔",     regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  "citrus-heights":    { name: "Citrus Heights",    zhName: "柑橘高地",     regionSlug: "sacramento-area", testimonialRegion: "sacramento" },
  // LA Cities
  "santa-monica":      { name: "Santa Monica",      zhName: "圣塔莫尼卡",   regionSlug: "los-angeles", testimonialRegion: "la" },
  "pasadena":          { name: "Pasadena",          zhName: "帕萨迪纳",     regionSlug: "los-angeles", testimonialRegion: "la" },
  "manhattan-beach":   { name: "Manhattan Beach",   zhName: "曼哈顿海滩",   regionSlug: "los-angeles", testimonialRegion: "la" },
  "long-beach":        { name: "Long Beach",        zhName: "长滩",         regionSlug: "los-angeles", testimonialRegion: "la" },
  "encino":            { name: "Encino",            zhName: "恩西诺",       regionSlug: "los-angeles", testimonialRegion: "la" },
  "studio-city":       { name: "Studio City",       zhName: "斯图迪奥城",   regionSlug: "los-angeles", testimonialRegion: "la" },
  "woodland-hills":    { name: "Woodland Hills",    zhName: "伍德兰山",     regionSlug: "los-angeles", testimonialRegion: "la" },
  // Other Cities
  "oakland":           { name: "Oakland",           zhName: "奥克兰",       regionSlug: "alameda", testimonialRegion: "bay-area" },
  "berkeley":          { name: "Berkeley",          zhName: "伯克利",       regionSlug: "alameda", testimonialRegion: "bay-area" },
  "fremont":           { name: "Fremont",           zhName: "弗里蒙特",     regionSlug: "alameda", testimonialRegion: "bay-area" },
  "hayward":           { name: "Hayward",           zhName: "海沃德",       regionSlug: "alameda", testimonialRegion: "bay-area" },
  "san-jose":          { name: "San Jose",          zhName: "圣何塞",       regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "palo-alto":         { name: "Palo Alto",         zhName: "帕洛阿尔托",   regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "sunnyvale":         { name: "Sunnyvale",         zhName: "桑尼维尔",     regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "mountain-view":     { name: "Mountain View",     zhName: "山景城",       regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "cupertino":         { name: "Cupertino",         zhName: "库比蒂诺",     regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "santa-clara-city":  { name: "Santa Clara",       zhName: "圣克拉拉",     regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "milpitas":          { name: "Milpitas",          zhName: "米尔皮塔斯",   regionSlug: "santa-clara", testimonialRegion: "san-jose" },
  "walnut-creek":      { name: "Walnut Creek",      zhName: "核桃溪",       regionSlug: "contra-costa", testimonialRegion: "bay-area" },
  "concord":           { name: "Concord",           zhName: "康科德",       regionSlug: "contra-costa", testimonialRegion: "bay-area" },
  "pleasanton":        { name: "Pleasanton",        zhName: "普莱森顿",     regionSlug: "alameda", testimonialRegion: "bay-area" },
  "dublin":            { name: "Dublin",            zhName: "都柏林",       regionSlug: "alameda", testimonialRegion: "bay-area" },
  "livermore":         { name: "Livermore",         zhName: "利弗莫尔",     regionSlug: "alameda", testimonialRegion: "bay-area" },
  "san-diego-city":    { name: "San Diego",         zhName: "圣地亚哥",     regionSlug: "san-diego", testimonialRegion: "san-diego" },
  "irvine":            { name: "Irvine",            zhName: "尔湾",         regionSlug: "orange-county", testimonialRegion: "san-diego" },
  "anaheim":           { name: "Anaheim",           zhName: "阿纳海姆",     regionSlug: "orange-county", testimonialRegion: "san-diego" },
  "huntington-beach":  { name: "Huntington Beach",  zhName: "亨廷顿海滩",   regionSlug: "orange-county", testimonialRegion: "san-diego" },
};

const ChineseCityPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const cityData = citySlug ? CITY_DATA[citySlug] : null;
  if (!cityData) return <Navigate to="/zh/sacramento" replace />;

  const { zhName, name, regionSlug, testimonialRegion } = cityData;

  return (
    <LocationContext.Provider value={{ slug: citySlug!, name, type: "city", regionSlug }}>
      <SEO
        title={`${zhName}家庭清洁服务 — Uplevel Cleaning`}
        description={`${zhName}专业家庭清洁服务。固定价格，60秒在线预约，200%满意保证。`}
        keywords={`${zhName}清洁服务, ${zhName}家政服务, ${zhName}打扫卫生, ${zhName}深度清洁, uplevel cleaning`}
        url={`https://uplevelcleaning.com/zh/${citySlug}`}
        alternateUrl={`https://uplevelcleaning.com/locations/${citySlug}`}
        alternateLang="en-US"
        locationName={name}
        regionSlug={regionSlug}
        lang="zh"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "首页", url: "/" },
          { name: "中文", url: "/zh/sacramento" },
          { name: zhName, url: `/zh/${citySlug}` }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="zh">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <ChineseHero cityName={zhName} regionSlug={regionSlug} />
          <ChineseWhyUs cityName={zhName} />
          <ChineseServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <ChineseTestimonials region={testimonialRegion} />
          <Guarantee />
          <ChineseReferFriend />
          <ChineseFAQ cityName={zhName} />
          <ChineseFinalCTA cityName={zhName} />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default ChineseCityPage;
